import { action, makeObservable, observable, runInAction } from 'mobx';
import { APIClient } from '../apiclient/APIClient';
import {
  AcceptOrderAcknowledgementResponse,
  CreateOrderResponse,
  CreatePlaidBankAccountResponse,
  CurrencyValue,
  FetchEligibilityResponse,
  FetchIdentityVerificationResponse,
  FetchOrderAcknowledgementsResponse,
  FetchOrderDocumentsResponse,
  FetchOrderResponse,
  FetchPaymentMethodsResponse,
  FetchPlaidLinkTokenResponse,
  ReviewOrderResponse,
  StartIdentityVerificationResponse,
  SubmitOrderResponse,
  SyncIdentityVerificationResponse,
  UpdateOrderResponse,
} from '../apiclient/APIClientInterface';
import { IdentityVerificationRoutes } from '../routes/IdentityVerificationRoutes';
import { InvestStateInterface, InvestStep } from './InvestStoreInterface';

const initialState: InvestStateInterface = {
  accountId: null,
  acknowledgementsFetched: false,
  currentStep: 'amount',
  documentsFetched: false,
  eligibility: null,
  identityVerification: null,
  identityVerificationFetched: false,
  identityVerificationId: null,
  identityVerificationLinkToken: null,
  identityVerificationSessionFinished: false,
  investmentAmount: null,
  isCreatingBankAccount: false,
  isCreatingOrder: false,
  isFetchingAcknowledgements: false,
  isFetchingDocuments: false,
  isFetchingEligibility: false,
  isFetchingIdentityVerification: false,
  isFetchingLinkToken: false,
  isFetchingOrder: false,
  isFetchingPaymentMethods: false,
  isStartingIdentityVerification: false,
  isSubmitting: false,
  isUpdatingOrder: false,
  order: null,
  orderAcknowledgements: [],
  orderDocuments: [],
  paymentMethods: [],
  plaidLinkToken: null,
  selectedPaymentMethodId: null,
};

export class InvestStore {
  @observable state: InvestStateInterface = { ...initialState };

  constructor() {
    makeObservable(this);
  }

  @action
  acceptAcknowledgement = async (acknowledgementId: string): Promise<AcceptOrderAcknowledgementResponse> => {
    if (!this.state.order) {
      return { success: false } as AcceptOrderAcknowledgementResponse;
    }
    const response = await APIClient.acceptOrderAcknowledgement({
      orderId: `${this.state.order.id}`,
      acknowledgementId,
    });

    runInAction(() => {
      if (response.success) {
        this.state.orderAcknowledgements = this.state.orderAcknowledgements.map(ack =>
          (ack.id === parseInt(acknowledgementId, 10)
            ? { ...ack, accepted: true, accepted_at: new Date().toISOString() }
            : ack)
        );
      }
    });

    return response;
  };

  @action
  createOrder = async (): Promise<CreateOrderResponse> => {
    if (!this.state.accountId) {
      return { success: false } as CreateOrderResponse;
    }

    this.state.isCreatingOrder = true;
    const response = await APIClient.createOrder({ accountId: this.state.accountId });

    runInAction(() => {
      if (response.success && response.order) {
        this.state.order = response.order;
      }
      this.state.isCreatingOrder = false;
    });

    return response;
  };

  @action
  createPlaidBankAccount = async (
    plaidAccountId: string,
    publicToken: string
  ): Promise<CreatePlaidBankAccountResponse> => {
    if (!this.state.order) {
      return { success: false } as CreatePlaidBankAccountResponse;
    }

    this.state.isCreatingBankAccount = true;
    const response = await APIClient.createPlaidBankAccount({
      investment_account_id: this.state.order.investment_account_id,
      plaid_account_id: plaidAccountId,
      public_token: publicToken,
    });

    if (response.success) {
      await this.fetchPaymentMethods();
    }

    runInAction(() => {
      this.state.isCreatingBankAccount = false;
    });

    return response;
  };

  @action
  fetchEligibility = async (): Promise<FetchEligibilityResponse> => {
    if (!this.state.accountId) {
      return { success: false } as FetchEligibilityResponse;
    }

    this.state.isFetchingEligibility = true;
    const response = await APIClient.fetchEligibility({ accountId: this.state.accountId });

    runInAction(() => {
      if (response.success && response.eligibility) {
        this.state.eligibility = response.eligibility;
      }
      this.state.isFetchingEligibility = false;
    });

    return response;
  };

  @action
  fetchIdentityVerification = async (
    routes?: IdentityVerificationRoutes
  ): Promise<FetchIdentityVerificationResponse> => {
    this.state.isFetchingIdentityVerification = true;
    const response = await APIClient.fetchIdentityVerification({ routes });

    runInAction(() => {
      if (response.success && response.verification) {
        this.state.identityVerification = response.verification;
      }
      this.state.identityVerificationFetched = true;
      this.state.isFetchingIdentityVerification = false;
    });

    return response;
  };

  @action
  fetchOrder = async (orderId: string): Promise<FetchOrderResponse> => {
    this.state.isFetchingOrder = true;
    const response = await APIClient.fetchOrder({ orderId });

    runInAction(() => {
      if (response.success && response.order) {
        this.state.order = response.order;
      }
      this.state.isFetchingOrder = false;
    });

    return response;
  };

  @action
  fetchOrderAcknowledgements = async (): Promise<FetchOrderAcknowledgementsResponse> => {
    if (!this.state.order) {
      return { success: false } as FetchOrderAcknowledgementsResponse;
    }

    this.state.isFetchingAcknowledgements = true;
    const response = await APIClient.fetchOrderAcknowledgements({ orderId: `${this.state.order.id}` });

    runInAction(() => {
      if (response.success && response.acknowledgements) {
        this.state.orderAcknowledgements = response.acknowledgements;
      }
      this.state.acknowledgementsFetched = true;
      this.state.isFetchingAcknowledgements = false;
    });

    return response;
  };

  @action
  fetchOrderDocuments = async (): Promise<FetchOrderDocumentsResponse> => {
    if (!this.state.order) {
      return { success: false } as FetchOrderDocumentsResponse;
    }

    this.state.isFetchingDocuments = true;
    const response = await APIClient.fetchOrderDocuments({ orderId: `${this.state.order.id}` });

    runInAction(() => {
      if (response.success && response.documents) {
        this.state.orderDocuments = response.documents;
      }
      this.state.documentsFetched = true;
      this.state.isFetchingDocuments = false;
    });

    return response;
  };

  @action
  fetchPaymentMethods = async (): Promise<FetchPaymentMethodsResponse> => {
    if (!this.state.order) {
      return { success: false } as FetchPaymentMethodsResponse;
    }

    this.state.isFetchingPaymentMethods = true;
    const response = await APIClient.fetchPaymentMethods({ orderId: `${this.state.order.id}` });

    runInAction(() => {
      if (response.success && response.payment_methods) {
        this.state.paymentMethods = response.payment_methods;
      }
      this.state.isFetchingPaymentMethods = false;
    });

    return response;
  };

  @action
  fetchPlaidLinkToken = async (): Promise<FetchPlaidLinkTokenResponse> => {
    this.state.isFetchingLinkToken = true;
    const response = await APIClient.fetchPlaidLinkToken({});

    runInAction(() => {
      if (response.success && response.link_token) {
        this.state.plaidLinkToken = response.link_token;
      }
      this.state.isFetchingLinkToken = false;
    });

    return response;
  };

  @action
  finishIdentityVerificationSession = () => {
    this.state.identityVerification = null;
    this.state.identityVerificationSessionFinished = true;
    this.clearIdentityVerificationLinkToken();
  };

  @action
  reset = () => {
    this.state = { ...initialState };
  };

  @action
  resetIdentityVerificationSession = () => {
    this.state.identityVerification = null;
    this.state.identityVerificationSessionFinished = false;
    this.clearIdentityVerificationLinkToken();
  };

  @action
  reviewOrder = async (): Promise<ReviewOrderResponse> => {
    if (!this.state.order) {
      return { success: false } as ReviewOrderResponse;
    }

    this.state.isSubmitting = true;
    const response = await APIClient.reviewOrder({ orderId: `${this.state.order.id}` });

    runInAction(() => {
      if (response.success && response.order) {
        this.state.order = response.order;
      }
      this.state.isSubmitting = false;
    });

    return response;
  };

  @action
  setAccountId = (accountId: string) => {
    this.state.accountId = accountId;
  };

  @action
  setCurrentStep = (step: InvestStep) => {
    this.state.currentStep = step;
  };

  @action
  setInvestmentAmount = (amount: CurrencyValue) => {
    this.state.investmentAmount = amount;
  };

  @action
  setSelectedPaymentMethodId = (id: number | null) => {
    this.state.selectedPaymentMethodId = id;
  };

  @action
  clearIdentityVerificationLinkToken = () => {
    this.state.identityVerificationLinkToken = null;
    this.state.identityVerificationId = null;
  };

  @action
  startIdentityVerification = async (
    forceNew?: boolean,
    routes?: IdentityVerificationRoutes
  ): Promise<StartIdentityVerificationResponse> => {
    this.state.isStartingIdentityVerification = true;
    const response = await APIClient.startIdentityVerification({ force_new: forceNew, routes });

    runInAction(() => {
      if (response.success) {
        this.state.identityVerificationId = response.verification_id ?? null;
        this.state.identityVerificationLinkToken = response.link_token ?? null;
      }
      this.state.isStartingIdentityVerification = false;
    });

    return response;
  };

  @action
  submitOrder = async (): Promise<SubmitOrderResponse> => {
    if (!this.state.order) {
      return { success: false } as SubmitOrderResponse;
    }

    this.state.isSubmitting = true;
    const response = await APIClient.submitOrder({ orderId: `${this.state.order.id}` });

    runInAction(() => {
      if (response.success && response.order) {
        this.state.order = response.order;
      }
      this.state.isSubmitting = false;
    });

    return response;
  };

  @action
  syncIdentityVerification = async (
    verificationId: string,
    routes?: IdentityVerificationRoutes
  ): Promise<SyncIdentityVerificationResponse> => {
    const response = await APIClient.syncIdentityVerification({ verificationId, routes });

    runInAction(() => {
      if (response.success && response.verification) {
        this.state.identityVerification = response.verification;
      }
    });

    return response;
  };

  @action
  updateOrder = async (data: {
    investment_amount?: CurrencyValue;
    payment_method_id?: number;
  }): Promise<UpdateOrderResponse> => {
    if (!this.state.order) {
      return { success: false } as UpdateOrderResponse;
    }

    this.state.isUpdatingOrder = true;
    const response = await APIClient.updateOrder({
      orderId: `${this.state.order.id}`,
      ...data,
    });

    runInAction(() => {
      if (response.success && response.order) {
        this.state.order = response.order;
      }
      this.state.isUpdatingOrder = false;
    });

    return response;
  };
}
